import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  OnDestroy,
  output,
  OutputEmitterRef,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faMicrochipAi } from '@fortawesome/pro-duotone-svg-icons';

/**
 * Recording-consent prompt: the transcription bot is in the room, so the user
 * is being recorded and their microphone stays muted until they confirm.
 *
 * A native `<dialog>` opened with `showModal()` — the top layer keeps it
 * visible and interactive even while a video tile is fullscreen (itself a
 * modal dialog that would leave any overlay inside the conference panel
 * invisible and inert), and gives focus trapping, Escape handling and the
 * dialog role for free. Purely presentational: the consent state machine lives
 * in `OdinService`; the parent mounts this while
 * `recordingConsentPromptVisible()` holds and destroys it otherwise, so the
 * dialog vanishes on its own when the bot leaves or consent is given elsewhere.
 */
@Component({
  selector: 'andor-recording-consent-dialog',
  standalone: true,
  imports: [TranslatePipe, FontAwesomeModule],
  templateUrl: './recording-consent-dialog.html',
  styleUrl: './recording-consent-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecordingConsentDialog implements AfterViewInit, OnDestroy {
  /** Display name of whoever started the recording, when known (empty for instant rooms). */
  startedByName = input('');
  /** "I understand" — the user consents to being recorded for this session. */
  consented = output<void>();
  /** "Stay muted" / Escape / backdrop — no consent; the mic stays gated. */
  dismissed = output<void>();

  private readonly _dialog = viewChild.required<ElementRef<HTMLDialogElement>>('dialog');
  // Exactly one outcome per mount: a button click also triggers the native
  // `close` event later, and the parent's teardown closes the dialog too.
  private _resolved = false;

  protected readonly faMicrochipAi = faMicrochipAi;

  ngAfterViewInit(): void {
    try {
      this._dialog().nativeElement.showModal();
    } catch (e) {
      console.warn('failed to open the recording consent dialog', e);
    }
  }

  ngOnDestroy(): void {
    // Torn down by the parent (bot left, consent given elsewhere, panel
    // unmounted for a reconnect) — never a decision, so never emit.
    this._resolved = true;
    const dialog = this._dialog().nativeElement;
    if (dialog.open) dialog.close();
  }

  protected consent(): void {
    this.resolve(this.consented);
  }

  protected dismiss(): void {
    this.resolve(this.dismissed);
  }

  /** Native close (Escape) — a dismissal unless a button already decided. */
  protected onClose(): void {
    if (!this._resolved) this.resolve(this.dismissed);
  }

  /** A click on `::backdrop` lands on the `<dialog>` itself; the card swallows its own. */
  protected onDialogClick(event: MouseEvent): void {
    if (event.target === this._dialog().nativeElement) this.dismiss();
  }

  private resolve(outcome: OutputEmitterRef<void>): void {
    if (this._resolved) return;
    this._resolved = true;
    outcome.emit();
  }
}
